'use client';

/**
 * PlanCard — live checklist rendered from the `update_plan` tool.
 *
 * The model calls `update_plan` with the FULL step list each time anything
 * changes (TodoWrite-style); the UI renders only the latest call per turn, so
 * the checklist updates in place instead of stacking cards.
 *
 * It stays OUTSIDE the activity strip (it is the roadmap for the work, not part
 * of it) but it does not stay EXPANDED forever: a finished 8-step plan is 8
 * lines of struck-through text above an answer nobody has read yet, on every
 * scroll past it. So a plan with no work left collapses to its one-line
 * receipt — reopenable, and never auto-collapsed a second time if the user
 * opens it back up.
 *
 * Plan shape/parsing lives in `lib/plan` (shared with the Hub tool registry)
 * so the tool contract and this renderer can never drift apart.
 */

import React from 'react';
import { CheckIcon, ChevronDownIcon, CircleIcon, Loader2Icon } from 'lucide-react';
import type { PlanStepStatus, UpdatePlanInput } from '../../lib/plan/index.js';

export type PlanCardProps = UpdatePlanInput;

function StepIcon({ status }: { status: PlanStepStatus }) {
  if (status === 'completed') {
    return <CheckIcon className="size-3.5 text-green-600 dark:text-green-500" aria-label="done" />;
  }
  if (status === 'in_progress') {
    return <Loader2Icon className="size-3.5 animate-spin text-primary" aria-label="in progress" />;
  }
  return <CircleIcon className="size-3 text-muted-foreground/60" aria-label="pending" />;
}

export function PlanCard({ title, steps }: PlanCardProps) {
  const doneCount = steps.filter((s) => s.status === 'completed').length;
  const isComplete = steps.length > 0 && doneCount === steps.length;

  const [isOpen, setIsOpen] = React.useState(!isComplete);
  const collapsedOnFinish = React.useRef(false);
  React.useEffect(() => {
    if (isComplete && !collapsedOnFinish.current) {
      collapsedOnFinish.current = true;
      setIsOpen(false);
    }
  }, [isComplete]);

  return (
    <div className="mt-3 rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full min-h-9 flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted/50 transition-colors text-left"
        aria-expanded={isOpen}
      >
        {isComplete && (
          <CheckIcon
            className="size-3.5 flex-shrink-0 text-green-600 dark:text-green-500"
            aria-hidden
          />
        )}
        <span className="font-medium min-w-0 truncate">{title ?? 'Plan'}</span>
        <span className="ml-auto flex items-center gap-2 flex-shrink-0 text-xs text-muted-foreground tabular-nums">
          <span>
            {doneCount} of {steps.length} done
          </span>
          <ChevronDownIcon className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {isOpen && (
        <ul className="border-t border-border/60 px-3 py-2 space-y-1.5">
          {steps.map((step, i) => (
            <li key={`${i}-${step.label}`} className="flex items-baseline gap-2 text-sm">
              <span className="flex-shrink-0 translate-y-0.5">
                <StepIcon status={step.status} />
              </span>
              <span
                className={
                  step.status === 'completed'
                    ? 'text-muted-foreground line-through decoration-border'
                    : step.status === 'in_progress'
                      ? 'font-medium'
                      : 'text-muted-foreground'
                }
              >
                {step.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
